// @ts-strict-ignore
import { DashboardCard } from "@dashboard/components/Card";
import { Divider } from "@dashboard/components/Divider";
import useDateLocalize from "@dashboard/hooks/useDateLocalize";
import { Box, Checkbox, Text, Tooltip } from "@saleor/macaw-ui-next";
import { Info } from "lucide-react";
import { Fragment, ReactNode } from "react";
import { useIntl } from "react-intl";

import { variantDetailsChannelsAvailabilityCardMessages as messages } from "./../messages";

const InfoTooltip = ({ content }: { content: ReactNode }) => (
  <Tooltip>
    <Tooltip.Trigger>
      <Box as="span" display="inline-flex" marginLeft={1} cursor="pointer">
        <Info size={16} />
      </Box>
    </Tooltip.Trigger>
    <Tooltip.Content side="top">
      <Tooltip.Arrow />
      <Box __maxWidth={320}>{content}</Box>
    </Tooltip.Content>
  </Tooltip>
);

interface ChannelsListItemProps {
  id: string;
  name: string;
  isPublished: boolean;
  publishedAt: string;
  // Variant-level listing control. When provided, the row exposes a per-variant
  // "For sale in this channel" toggle (channel listing existence) and shows the
  // product publication status as read-only context.
  isListed?: boolean;
  onToggleListed?: (next: boolean) => void;
  disabled?: boolean;
}

export const ChannelsListItem = ({
  id,
  name,
  isPublished,
  publishedAt,
  isListed,
  onToggleListed,
  disabled,
}: ChannelsListItemProps) => {
  const intl = useIntl();
  const localizeDate = useDateLocalize();
  const hasListingControl = typeof onToggleListed === "function";
  const getItemSubtitle = () => {
    if (hasListingControl) {
      if (!isPublished) {
        return intl.formatMessage(messages.itemSubtitleProductNotPublished);
      }

      return intl.formatMessage(messages.itemSubtitleProductPublished, {
        publishedAt: localizeDate(publishedAt),
      });
    }

    if (!isPublished) {
      return intl.formatMessage(messages.itemSubtitleHidden);
    }

    return intl.formatMessage(messages.itemSubtitlePublished, {
      publishedAt: localizeDate(publishedAt),
    });
  };

  return (
    <Fragment key={id}>
      <Divider />
      <DashboardCard.Content paddingY={6}>
        <Text
          as="p"
          size={3}
          fontWeight="bold"
          data-test-id={`channels-variant-availability-item-title-${id}`}
          display="block"
        >
          {name}
        </Text>
        <Box display="inline-flex" alignItems="center">
          <Text size={3} data-test-id={`channels-variant-availability-item-subtitle-${id}`}>
            {getItemSubtitle()}
          </Text>
          {hasListingControl && (
            <InfoTooltip content={intl.formatMessage(messages.productPublicationTooltip)} />
          )}
        </Box>
        {hasListingControl && (
          <Box marginTop={2} display="flex" alignItems="center">
            <Checkbox
              checked={!!isListed}
              disabled={disabled}
              onCheckedChange={checked => onToggleListed(checked === true)}
              data-test-id={`channels-variant-availability-item-listed-${id}`}
            >
              <Text size={3}>{intl.formatMessage(messages.variantForSaleLabel)}</Text>
            </Checkbox>
            <InfoTooltip content={intl.formatMessage(messages.variantForSaleTooltip)} />
          </Box>
        )}
      </DashboardCard.Content>
    </Fragment>
  );
};
